import { useEffect, useRef, useState, KeyboardEvent } from 'react';
import { SparklesIcon } from '@heroicons/react/24/solid';
import {
  PaperAirplaneIcon,
  PaperClipIcon,
  XMarkIcon,
  DocumentIcon,
  ArrowDownTrayIcon,
} from '@heroicons/react/24/outline';
import { cn } from '../../../lib/utils';
import { useAuthStore } from '../../../stores/authStore';
import type { TeamMessage } from '../../../lib/websocket/events';
import { useTeamChat } from '../hooks/useTeamChat';
import { uploadFile, UploadedMedia } from '../../../lib/api';
import { APP_NAME } from '@/stores/partner.store';

function TeamMedia({ msg }: { msg: TeamMessage }) {
  if (!msg.mediaUrl) return null;
  if (msg.mediaType === 'image') {
    return (
      <a href={msg.mediaUrl} target="_blank" rel="noopener noreferrer" className="mt-1 block">
        <img
          src={msg.mediaUrl}
          alt={msg.fileName || 'Shared image'}
          className="max-h-56 max-w-full rounded-lg object-contain"
          loading="lazy"
        />
      </a>
    );
  }
  if (msg.mediaType === 'video') {
    return (
      <video src={msg.mediaUrl} controls className="mt-1 max-h-56 max-w-full rounded-lg" preload="metadata" />
    );
  }
  return (
    <a
      href={msg.mediaUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="mt-1 flex items-center gap-2 rounded-card border border-line px-2.5 py-2 text-sm hover:bg-black/[0.04]"
    >
      <DocumentIcon className="h-5 w-5 shrink-0" />
      <span className="truncate">{msg.fileName || 'Attachment'}</span>
      <ArrowDownTrayIcon className="h-4 w-4 shrink-0" />
    </a>
  );
}

function initials(name: string | null): string {
  if (!name) return '?';
  return name
    .split(' ')
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

function formatTime(iso: string | null): string {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
}

function MessageBubble({ msg, isMe }: { msg: TeamMessage; isMe: boolean }) {
  const isAgent = msg.sender === 'agent';
  const isSystem = msg.sender === 'system';

  if (isSystem) {
    return (
      <div className="my-2 text-center text-xs text-ink-3">{msg.content}</div>
    );
  }

  return (
    <div className={cn('flex gap-2.5', isMe ? 'flex-row-reverse' : 'flex-row')}>
      {/* Avatar */}
      <div
        className={cn(
          'mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-xs font-semibold',
          isAgent
            ? 'bg-brand text-white'
            : isMe
            ? 'bg-brand/10 text-brand'
            : 'bg-surface-2 text-ink-2'
        )}
      >
        {isAgent ? <SparklesIcon className="h-4 w-4" /> : initials(msg.senderName)}
      </div>

      {/* Bubble */}
      <div className={cn('flex max-w-[75%] flex-col', isMe ? 'items-end' : 'items-start')}>
        <div className="mb-0.5 flex items-center gap-2 px-1">
          <span className={cn('text-xs font-medium', isAgent ? 'text-brand' : 'text-ink-2')}>
            {isAgent ? APP_NAME : isMe ? 'You' : msg.senderName || 'Teammate'}
          </span>
          <span className="text-[10px] text-ink-3">{formatTime(msg.createdAt)}</span>
        </div>
        <div
          className={cn(
            'whitespace-pre-wrap break-words rounded-2xl px-3.5 py-2 text-sm',
            isAgent
              ? 'bg-brand/10 text-ink ring-1 ring-brand/20'
              : isMe
              ? 'bg-brand text-white'
              : 'bg-surface-2 text-ink'
          )}
        >
          {msg.content && <span>{msg.content}</span>}
          <TeamMedia msg={msg} />
        </div>
      </div>
    </div>
  );
}

export default function TeamChat() {
  const currentUserId = useAuthStore((s) => s.user?.id ?? null);
  const { channel, messages, isLoading, sending, send } = useTeamChat();
  const [draft, setDraft] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length]);

  const handleSend = async () => {
    const text = draft.trim();
    if ((!text && !file) || sending || uploading) return;

    let media: UploadedMedia | undefined;
    if (file) {
      setUploading(true);
      setUploadError(null);
      try {
        media = await uploadFile(file);
      } catch (err: any) {
        setUploadError(err?.response?.data?.detail || 'Upload failed. Please try again.');
        setUploading(false);
        return;
      }
      setUploading(false);
    }

    setDraft('');
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    await send(text, media);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    // Enter inserts a new line (default textarea behavior). Send with the
    // button or ⌘/Ctrl+Enter.
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-line px-6 py-4">
        <div>
          <h1 className="font-display text-lg font-semibold text-ink">
            Team Chat{channel?.name ? ` · ${channel.name}` : ''}
          </h1>
          <p className="text-xs text-ink-3">
            Everyone on the team is here. Mention{' '}
            <span className="font-medium text-brand">@pulse</span> to ask the AI.
          </p>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 space-y-4 overflow-y-auto px-6 py-4">
        {isLoading ? (
          <div className="flex h-full items-center justify-center text-sm text-ink-3">
            Loading…
          </div>
        ) : messages.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center gap-2 text-center text-ink-3">
            <SparklesIcon className="h-8 w-8 text-ink-3" />
            <p className="text-sm">No messages yet.</p>
            <p className="text-xs">
              Say hi to the team, or ask <span className="text-brand">@pulse</span> for a status update.
            </p>
          </div>
        ) : (
          messages.map((msg) => (
            <MessageBubble key={msg.id} msg={msg} isMe={!!currentUserId && msg.senderId === currentUserId} />
          ))
        )}
        <div ref={bottomRef} />
      </div>

      {/* Composer */}
      <div className="border-t border-line px-6 py-4">
        {file && (
          <div className="mb-2 flex items-center gap-2 rounded-card border border-line bg-surface-2 px-3 py-2 text-sm text-ink">
            <PaperClipIcon className="h-4 w-4 shrink-0 text-ink-3" />
            <span className="truncate">{file.name}</span>
            <span className="shrink-0 text-xs text-ink-3">{(file.size / 1024).toFixed(0)} KB</span>
            <button
              type="button"
              onClick={() => {
                setFile(null);
                if (fileInputRef.current) fileInputRef.current.value = '';
              }}
              className="ml-auto shrink-0 rounded p-0.5 text-ink-2 hover:bg-black/[0.04] hover:text-ink"
              aria-label="Remove attachment"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>
        )}
        {uploadError && <p className="mb-2 text-xs text-danger">{uploadError}</p>}
        <div className="flex items-end gap-3">
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            onChange={(e) => {
              setFile(e.target.files?.[0] || null);
              setUploadError(null);
            }}
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={sending || uploading}
            title="Attach a file"
            aria-label="Attach a file"
            className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl border border-line bg-surface text-ink-2 transition-colors hover:bg-black/[0.04] hover:text-ink disabled:cursor-not-allowed disabled:opacity-40"
          >
            <PaperClipIcon className="h-5 w-5" />
          </button>
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={handleKeyDown}
            rows={1}
            placeholder="Message the team… (@pulse to ask the AI)"
            title="Enter for a new line · ⌘/Ctrl+Enter to send"
            className="max-h-40 flex-1 resize-none rounded-xl border border-line bg-surface px-4 py-2.5 text-sm text-ink placeholder-ink-3 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand/20"
          />
          <button
            onClick={handleSend}
            disabled={(!draft.trim() && !file) || sending || uploading}
            className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-brand text-white transition-colors hover:bg-brand-ink disabled:cursor-not-allowed disabled:opacity-40"
          >
            <PaperAirplaneIcon className="h-5 w-5" />
          </button>
        </div>
      </div>
    </div>
  );
}
